import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useApplyResetToCaches } from 'src/components/dispatch/useApplyResetToCaches';
import { DeliveryOperationRequest } from 'src/entities/DeliveryOperationRequest.entity';
import { PlanningEntity } from 'src/entities/planningEntity';
import { PlanningSettingRequest } from 'src/entities/PlanningHistory.entity';
import { AsyncPlanningResponseEntity } from 'src/entities/planningResponseEntity';
import { PlanningRunningEntity } from 'src/entities/PlanningsRunning.entity';
import { SelectedCycle } from 'src/entities/SelectedCycle.entity';
import { ShipmentOrderLegRunListResponse } from 'src/entities/ShipmentOrderLegRun.entity';
import { TimelineRequestEntity } from 'src/entities/TimelineRequest.entity';
import {
  CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY,
  CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY,
} from 'src/hooks/useConvenienceDispatchDeliveries.query';
import { useQueryLatestAlgorithmRequestVersion } from 'src/hooks/useQueryLatestAlgorithmRequestVersion';
import { useMutationPlanningV5, useScenarioPlanningV5 } from 'src/hooks/useQueryPlanning';
import { useQueryPlanningsResultsV5 } from 'src/hooks/useQueryPlanningsResults';
import {
  clearRunningPlanningsCache,
  invalidateRunningPlanningsCache,
  useMutationPlanningRunnings,
  useQueryPlanningsRunnings,
} from 'src/hooks/useQueryPlanningsRunnings';
import { SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY } from 'src/hooks/useQueryShipmentOrderLegRuns';

import { AutoDispatchMode, normalizeInitialResponse, normalizePollingResult } from './normalizeResponse';
import { isSameRun, RunIdentity, RunScope } from './runIdentity';

/** 設計書 `:626-634` が正本。**version を落とさないこと** */
export type AutoDispatchResult = {
  /** 割り当たらなかった legRun。timeline の即時完了と skipped では空 */
  notAllocatedOrderIds: number[];
  /** 割当不可の理由参照に使う。取れなければ null */
  sessionId: string | null;
  /** 完了時に controller が自分の保持を更新する。取れなければ null */
  latestAlgorithmRequestVersion: number | null;
};

export type AutoDispatchHooks = {
  onRunAccepted: (context: { mode: AutoDispatchMode }) => void;
  onInitialResponse?: (context: { mode: AutoDispatchMode }) => void;
  onStarted: (context: { mode: AutoDispatchMode; orderIds: number[] }) => void;
  shouldClearLocalOverrides: () => boolean;
  clearLocalOverrides: () => void;
  onCompleted: (
    result: AutoDispatchResult,
    context: { mode: AutoDispatchMode; completionKind: 'immediate' | 'queued' | 'skipped' },
  ) => void;
  onScopeChanged: () => void;
  onFailed: (
    error: unknown,
    context: { mode: AutoDispatchMode; phase: 'reset' | 'request' | 'polling' | 'cancel' },
  ) => void;
  /**
   * 停止したとき（実行中だった場合のみ）。onFailed は override の破棄まで巻き添えで
   * 走るため cancel からは撃たない一方、停止でも「送った案件を未割当へ戻す」ような
   * 後始末は必要なので、専用の hook で知らせる。
   */
  onCancelled?: (context: { mode: AutoDispatchMode }) => void;
};

/** `useQueryPlanning.ts:404-413` の normalPlanning から version を抜いたもの */
export type RunNormalInput = {
  shiftIds: number[];
  orderIds: number[];
  planning: PlanningEntity;
  requestAsync: boolean;
  selectedCycleIndexes: SelectedCycle[];
  deliveryOperations: DeliveryOperationRequest[];
  planningSetting: PlanningSettingRequest;
  /** mutation では optional。カード配車は渡さないが、DTO は mutate 引数と 1 対 1 にする */
  forcePlanning?: boolean;
};

/** `useQueryPlanning.ts:395-400` の transferPlanning から version を抜いたもの */
export type RunTransferInput = {
  orderIds: number[];
  transferToShiftId: number;
  transferFromShiftId: number;
  planning: PlanningEntity;
};

/**
 * `useQueryPlanning.ts:463-465` の timelinePlanning から version・startOn・endOn を抜き、
 * 前段 reset の対象（現行の `unassignedDropIds`・`Presenter.tsx:1810-1832`）を足したもの。
 * startOn / endOn は scope から補う。
 */
export type RunTimelineInput = {
  timelineEntities: TimelineRequestEntity[];
  emptyDeliveryIds: number[];
  resetLegRunIds: number[];
  /**
   * reset 対象を除いたあと、他に変更が残っていないか（現行 `:1835` の
   * `changedLegRunIds.size === 0`）。**controller 側では導出できない**ので画面が渡す。
   * 詳細は Task 6「終わり方は 3 つある」。
   */
  isUnassignOnly: boolean;
};

/** `useScenarioPlanningRequestV5.ts:26` から startOn / endOn を抜いたもの（scope から補う） */
export type RunScenarioInput = { scenarioId: number };

/** 入れ替える 2 勤務。対象の解決は画面側 (resolveSwapTargets) */
export type RunSwapInput = { deliveryIds: [number, number] };

/** polling クエリの結果。`normalizePollingResult` の引数と同じ緩さ（テストで部分形を渡すため） */
type PollingQueryResult = {
  data: {
    not_allocated_order_ids?: number[];
    latest_algorithm_request_version?: number;
    session_id?: string;
  } | undefined;
  error: unknown;
};

/** normal/transfer の初期応答（HTTP 2xx）の生の形。normalizeInitialResponse の引数と同じ緩さ */
type RawInitialResponse = {
  status?: string;
  session_id?: string;
  scenario_planning_id?: string;
  respect_recent_id?: string;
  not_allocated_order_ids?: number[];
};

/** mutate の resultHandlers。実 useMutation の onSuccess/onError と同じ形（余分な引数は無視できる） */
type MutateResultHandlers = {
  onSuccess: (response: { data: RawInitialResponse }) => void;
  onError: (error: unknown) => void;
};

/** cancel の DELETE mutation。実 useMutationPlanningRunnings().delete と同じ形 */
type CancelRunningMutation = {
  mutate: (
    variables: { id: number; startOn: string; endOn: string },
    options?: { onSuccess?: () => void; onError?: (error: unknown) => void },
  ) => void;
};

type NormalPlanningMutation = {
  mutate: (variables: RunNormalInput & { latestAlgorithmRequestVersion: number }, options: MutateResultHandlers) => void;
};

type TransferPlanningMutation = {
  mutate: (variables: RunTransferInput & { latestAlgorithmRequestVersion: number }, options: MutateResultHandlers) => void;
};

/** timeline は startOn/endOn も自分で持つ（scope から補う。normal/transfer は planning に含む） */
type TimelinePlanningMutation = {
  mutate: (
    variables: {
      startOn: string; endOn: string; latestAlgorithmRequestVersion: number;
      timelineEntities: TimelineRequestEntity[]; emptyDeliveryIds: number[];
    },
    options: MutateResultHandlers,
  ) => void;
};

/** swap は timeline と同じく startOn/endOn を自分で持つ（scope から補う） */
type SwapPlanningMutation = {
  mutate: (
    variables: {
      deliveryIds: [number, number]; latestAlgorithmRequestVersion: number;
      startOn: string; endOn: string;
    },
    options: MutateResultHandlers,
  ) => void;
};

type ResettingMutation = {
  mutateAsync: (orderIds: number[]) => Promise<unknown>;
};

type PlanningMutations = {
  normalPlanning: NormalPlanningMutation;
  transferPlanning: TransferPlanningMutation;
  timelinePlanning: TimelinePlanningMutation;
  swapPlanning: SwapPlanningMutation;
  operationBulkResetting: ResettingMutation;
};

type VersionQueryResult = { data: number | undefined; refetch: () => Promise<unknown> };

/** running 一覧クエリ。timeline の queued 分岐だけが呼ぶ（下記 runningPlanningIdRef 参照） */
type RunningPlanningsQueryResult = { refetch: () => Promise<{ data?: PlanningRunningEntity[] }> };

/** normal/transfer/timeline とは別フック（useScenarioPlanningV5）なので独立させる */
type ScenarioPlanningMutation = {
  mutate: (variables: RunScenarioInput & { startOn: string; endOn: string }, options: MutateResultHandlers) => void;
};

/**
 * react-query 依存を hook の引数で受ける。`renderHook` から差し込めるようにするため
 * （実クエリ・mutation は HTTP を挟むため直接はテストできない）。
 */
export type AutoDispatchDeps = {
  usePollingQuery: (request: AsyncPlanningResponseEntity | null) => PollingQueryResult;
  useCancelRunningMutation: () => CancelRunningMutation;
  usePlanningMutations: () => PlanningMutations;
  useVersionQuery: (startOn: string, endOn: string) => VersionQueryResult;
  useRunningPlanningsQuery: (startOn: string, endOn: string) => RunningPlanningsQueryResult;
  useScenarioMutation: () => ScenarioPlanningMutation;
};

const defaultDeps: AutoDispatchDeps = {
  usePollingQuery: (request) => useQueryPlanningsResultsV5(request),
  useCancelRunningMutation: () => useMutationPlanningRunnings().delete,
  usePlanningMutations: () => {
    const {
      normalPlanning, transferPlanning, timelinePlanning, swapPlanning, operationBulkResetting,
    } = useMutationPlanningV5();
    return { normalPlanning, transferPlanning, timelinePlanning, swapPlanning, operationBulkResetting };
  },
  useVersionQuery: (startOn, endOn) => useQueryLatestAlgorithmRequestVersion(startOn, endOn),
  useRunningPlanningsQuery: (startOn, endOn) => useQueryPlanningsRunnings(startOn, endOn),
  useScenarioMutation: () => useScenarioPlanningV5().scenarioPlanning,
};

/** polling 中のリクエストと、それを発行した run の同一性をセットで持つ */
type ActiveRequest = {
  payload: AsyncPlanningResponseEntity;
  run: RunIdentity;
};

export function useAutoDispatchController(params: {
  scope: RunScope;
  hooks: AutoDispatchHooks;
  deps?: Partial<AutoDispatchDeps>;
}): {
  isRunning: boolean;
  isReconciling: boolean;
  isBusy: boolean;
  /** サーバが受け付けて計算に入ったか。停止操作を出せるのはこの間だけ */
  isAccepted: boolean;
  /**
   * 走っている計算を止める。
   *
   * notifyOnFailure は「止められなかったことを onFailed で知らせるか」。既定は知らせない。
   * cancel はユーザーの停止操作以外（カード配車の override 変更検知、scope 変更の後始末）
   * からも呼ばれ、そこで onFailed を撃つと画面側の失敗処理（override の破棄など）が
   * 巻き添えで走るため、知らせるかどうかは呼び出し元が決める。
   */
  cancel: (options?: { notifyOnFailure?: boolean }) => void;
  runNormal: (input: RunNormalInput) => void;
  runTransfer: (input: RunTransferInput) => Promise<void>;
  runTimeline: (input: RunTimelineInput) => Promise<void>;
  runScenario: (input: RunScenarioInput) => void;
  runSwap: (input: RunSwapInput) => void;
} {
  const { scope, hooks } = params;
  const {
    usePollingQuery, useCancelRunningMutation, usePlanningMutations, useVersionQuery, useRunningPlanningsQuery,
    useScenarioMutation,
  } = { ...defaultDeps, ...params.deps };
  const queryClient = useQueryClient();
  const cancelRunningMutation = useCancelRunningMutation();
  const {
    normalPlanning: normalMutation,
    transferPlanning: transferMutation,
    timelinePlanning: timelineMutation,
    swapPlanning: swapMutation,
    operationBulkResetting: resettingMutation,
  } = usePlanningMutations();
  // useMutation の戻り値は毎レンダー新しいオブジェクト。mutate 自体は安定なので
  // 依存にはこちらを使う（オブジェクトを依存にすると runSwap が毎回作り直される）
  const swapMutate = swapMutation.mutate;
  // running 一覧の refetch。timeline の queued 分岐だけが呼ぶ（下記 runningPlanningIdRef 参照）
  const runningPlanningsQuery = useRunningPlanningsQuery(scope.startOn, scope.endOn);
  // reset 成功後、キャッシュへ即時反映する共有 hook（画面固有の state を持たないので DI しない。
  // 現行 :1824 相当。実体は src/components/dispatch/useApplyResetToCaches.ts）
  const applyResetToCaches = useApplyResetToCaches();
  const scenarioMutation = useScenarioMutation();

  const [isRunning, setIsRunning] = useState(false);
  const [isReconciling, setIsReconciling] = useState(false);
  const [activeRequest, setActiveRequest] = useState<ActiveRequest | null>(null);
  const [latestAlgorithmRequestVersion, setLatestAlgorithmRequestVersion] = useState(0);

  const generationRef = useRef(0);
  const activeRunRef = useRef<RunIdentity | null>(null);
  // scope はオブジェクトなので、参照ではなく値で比較する（参照比較だと毎レンダー発火する）
  const scopeKeyRef = useRef(`${scope.startOn}:${scope.endOn}:${scope.companyId}`);
  // 会社変更だけを日付変更と区別するために持つ（下記スコープ変更 effect 参照）
  const prevCompanyIdRef = useRef(scope.companyId);
  const hooksRef = useRef(hooks);
  hooksRef.current = hooks;

  // 実行中の mode。run* が始めるときに記録し、completion/onFailed で読む
  const activeModeRef = useRef<AutoDispatchMode | null>(null);
  // polling 完了 handler が現在処理中の目印。await 中にキャンセルされた場合、handler は
  // これが変わっている(=null になっている)ことを検知して残りの処理を中断する。
  // ref を消すのは cancel（設計書 :338-）
  const activePollingSessionRef = useRef<string | null>(null);
  // polling 中ジョブの対象日付（ジョブ開始時の startOn/endOn）。完了/キャンセル時の
  // running キャッシュ操作は、画面の現在表示日付ではなくこの「ジョブ開始時の日付」を使う。
  const runningPlanningRangeRef = useRef<{ startOn: string; endOn: string } | null>(null);
  // 自動配車キャンセル用: 実行中の Planning ID（DELETE /api/v3/plannings/running に渡す）。
  // 現行 `setRunningPlanningId` は Presenter.tsx:2006（timeline の onSuccess）の 1 箇所だけで、
  // normal/transfer には ID を取得する経路が無い。normal/transfer 経由では常に null のまま
  // （= cancel は DELETE を撃たず invalidate だけで終わる）。timeline の queued 分岐だけが埋める
  const runningPlanningIdRef = useRef<number | null>(null);

  // **useLayoutEffect であること（useEffect にしない）。**
  // useEffect だと render 後まで scopeRef が古いままで、scope が変わった render と
  // effect 実行の間に旧 callback が発火すると照合を通ってしまう（設計書 :313-336）。
  //
  // Why not このタスクのテストで検出できると期待しない: entry check（下記 isSameRun）は
  // scopeRef を「await より前」の 1 箇所でしか同期的に読まない。scopeRef 用 effect は
  // 完了 effect より前に宣言しているため、同一 flush サイクル内では useEffect でも
  // useLayoutEffect でも宣言順で先に走り、最新化されてしまう（実測で確認・Step 4-b 参照）。
  // それでも useLayoutEffect を選ぶのは、Task 4 以降で post-await の再照合を足したときに
  // 効いてくるため（設計書 :313-336 の一般原則を先取りして守る）。
  const scopeRef = useRef(scope);
  useLayoutEffect(() => { scopeRef.current = scope; }, [scope]);

  // 初期値: /api/v3/latest_algorithm_request_version から取得（現行 :1220-1234）。
  // mutation/polling 完了時に setLatestAlgorithmRequestVersion で上書きされる
  const versionQuery = useVersionQuery(scope.startOn, scope.endOn);
  const initialAlgorithmVersion = versionQuery.data;
  useEffect(() => {
    if (initialAlgorithmVersion != null) {
      setLatestAlgorithmRequestVersion(initialAlgorithmVersion);
    }
  }, [initialAlgorithmVersion, scope.startOn, scope.endOn]);

  // 実行中ジョブをキャンセルしても、既にサーバ側で取込が完了しているケース
  // (DELETE が 409＝取込済み) や ID 未取得で backend ジョブが走り切るケースでは
  // deliveries / legRuns の DB 状態が変わっている。再取得して所在を最新化しないと
  // 次の自動配車が古い状態から算出し、二重割り当てにつながる (log_web_issues#276)。
  // cancel() と、下記スコープ変更 effect の会社変更分岐の両方から呼ぶ
  const refreshServerDeliveries = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY] });
    void queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY] });
    void queryClient.invalidateQueries({ queryKey: [SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY] });
  }, [queryClient]);

  /**
   * 版番号を取り直す。
   *
   * API の版番号は「running でない planning の最大 id」(company.rb:398 /
   * planning.rb:99 の done スコープ)。止めると planning が cancelled になって
   * running から外れるので、その瞬間にサーバ側の版番号が上がる。取り直さないと
   * 次の自動配車が古い版で撃たれ「同時にリクエストが行われた可能性があるため、
   * 配車ができませんでした。」になる。
   */
  /** 取り直した版番号を返す。取れなければ渡された値をそのまま使う */
  const resolveFreshVersion = async (fallback: number): Promise<number> => {
    try {
      const refresh = await versionQuery.refetch() as { data?: unknown };
      if (typeof refresh.data === 'number') {
        setLatestAlgorithmRequestVersion(refresh.data);
        return refresh.data;
      }
    } catch (e) {
      console.warn('[autoDispatch] failed to refetch latest algorithm version (use cached)', e);
    }
    return fallback;
  };
  // useCallback 内から参照するため ref 越しにする（毎レンダー作り直される関数を
  // 依存に入れると runNormal/cancel が毎回作り直され、memo 済みの子が再描画される）
  const resolveFreshVersionRef = useRef(resolveFreshVersion);
  resolveFreshVersionRef.current = resolveFreshVersion;

  const refreshAlgorithmVersionRef = useRef<() => void>(() => undefined);
  const refreshAlgorithmVersion = () => { void resolveFreshVersion(latestAlgorithmRequestVersion); };

  refreshAlgorithmVersionRef.current = refreshAlgorithmVersion;

  // 実行中ジョブの DELETE を送る本体。cancel() と、下記スコープ変更 effect の
  // 会社変更分岐（旧実装の cancelRunningIfAny 相当）の両方から呼ぶ（Task 5 で
  // 確認済みの「まとめられるものはまとめる」方針）。応答を待つ間 isBusy を立てるかどうかは
  // 呼び出し側の責務にする（スコープ変更は「別 scope の後始末」なので、現在の isBusy には
  // 乗せない。cancel() 側だけ isReconciling を立てる）
  const sendCancelDelete = useCallback((
    id: number,
    range: { startOn: string; endOn: string },
    onSettled?: () => void,
    onFailure?: (error: unknown) => void,
  ) => {
    cancelRunningMutation.mutate(
      { id, startOn: range.startOn, endOn: range.endOn },
      {
        onSuccess: () => {
          onSettled?.();
          void clearRunningPlanningsCache(queryClient, range.startOn, range.endOn);
        },
        onError: (e: unknown) => {
          onSettled?.();
          console.warn('[autoDispatch] cancel API failed', e);
          // 失敗を利用者に伝えるかは呼び出し側の責務（onSettled と同じ分担）。
          // ユーザーの停止操作 = cancel() だけが渡す。スコープ変更の後始末は渡さない
          onFailure?.(e);
          void invalidateRunningPlanningsCache(queryClient, range.startOn, range.endOn);
          refreshServerDeliveries();
        },
      },
    );
  }, [queryClient, cancelRunningMutation, refreshServerDeliveries]);

  /**
   * 停止できなかったことの通知。取込中はサーバが board ロックを取れずに 500 を返し、
   * ジョブは止まらないまま結果が反映される。黙って捨てると画面だけ「止まった」ことに
   * なり、あとから盤面が動いた理由が分からない
   */
  const notifyCancelFailure = useCallback((error: unknown) => {
    hooksRef.current.onFailed(error, { mode: activeModeRef.current ?? 'normal', phase: 'cancel' });
  }, []);

  useEffect(() => {
    const key = `${scope.startOn}:${scope.endOn}:${scope.companyId}`;
    if (key === scopeKeyRef.current) return;
    scopeKeyRef.current = key;

    // 会社変更だけを区別する（下の DELETE 送信の分岐で使う）
    const companyChanged = prevCompanyIdRef.current !== scope.companyId;
    prevCompanyIdRef.current = scope.companyId;

    // 旧 run の callback を捨てる。generation を進めるだけで、
    // 進行中の await はすべて isSameRun で弾かれる
    generationRef.current += 1;
    activeRunRef.current = null;
    activePollingSessionRef.current = null;
    const prevId = runningPlanningIdRef.current;
    runningPlanningIdRef.current = null;
    setActiveRequest(null);
    setIsRunning(false);

    // 日付変更で元日付ジョブの polling は破棄されるため、その後バックエンドで元日付の
    // ジョブが完了/継続しても元日付の running キャッシュを更新できなくなる。ref を破棄する
    // 前に元日付のキャッシュを invalidate しておく（現行 :637-643）
    const prevRange = runningPlanningRangeRef.current;
    runningPlanningRangeRef.current = null;
    if (prevRange) {
      // 会社変更のときだけ、旧 run を backend でも止める（旧実装の cancelRunningIfAny 相当。
      // Task 8 レビューで検出した回帰の修正）。id は「旧 run の id」、range は「旧 run の日付」
      // （現在の scope の日付ではない。Task 6 の Critical の再発防止）。
      // 日付だけの変更は対象外（旧実装の日付専用 effect も DELETE を撃っていなかった。
      // 挙動不変の原則により、この差は今回は広げない）
      if (companyChanged && prevId !== null) {
        sendCancelDelete(prevId, prevRange);
      } else {
        void invalidateRunningPlanningsCache(queryClient, prevRange.startOn, prevRange.endOn);
      }
    }

    hooksRef.current.onScopeChanged();
  }, [scope.startOn, scope.endOn, scope.companyId, queryClient, sendCancelDelete]);

  const { data: planningResultData, error: planningResultError } = usePollingQuery(activeRequest?.payload ?? null);

  // 未割当だった legRun の legRuns キャッシュを assigned=false に直接書き換える（現行 :1311-1330 /
  // :2744-2763）。SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY は単体エンティティ cache と無限スクロール
  // cache の両方で使われているため、pages を持つものだけ対象にする。polling 完了と即時完了の
  // 両方で使うので共通化する
  const markLegRunsUnassigned = (notAllocIds: number[]) => {
    if (notAllocIds.length === 0) return;
    const notAllocIdSet = new Set<number>(notAllocIds);
    queryClient.setQueriesData<InfiniteData<ShipmentOrderLegRunListResponse>>(
      { queryKey: [SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY] },
      (old) => {
        if (!old) return old;
        const maybePages = (old as { pages?: unknown }).pages;
        if (!Array.isArray(maybePages)) return old;
        return {
          ...old,
          pages: old.pages.map((p) => ({
            ...p,
            data: p.data.map((lr) => (
              (lr.id != null && notAllocIdSet.has(lr.id)) ? { ...lr, assigned: false } : lr
            )),
          })),
        };
      },
    );
  };

  // 失敗時の復旧。移設元は resetAfterPlanningError（:1239-1258）という 1 つの関数を
  // request 失敗（normal/transfer）・前段 reset 失敗（transfer）・polling 失敗の
  // 3 経路すべてから呼んでいる。ここでも 1 つにまとめて 3 経路から呼ぶ（Task 5 レビューで
  // 指摘: 3 か所へインライン展開すると片方が欠ける実害があった）。
  // override 3 つ（画面所有）は含めない — onFailed 実装側が処理する（設計書「失敗時の分担」）
  const recoverAfterFailure = useCallback(() => {
    setActiveRequest(null);
    setIsRunning(false);
    activeRunRef.current = null;
    activePollingSessionRef.current = null;
    runningPlanningIdRef.current = null;

    // 対象はジョブ開始時の日付（実行中に表示日付を変えていても誤らない）
    const range = runningPlanningRangeRef.current;
    runningPlanningRangeRef.current = null;
    if (range) {
      void invalidateRunningPlanningsCache(queryClient, range.startOn, range.endOn);
    }
    // **この 4 つは無条件。** エラー時はサーバ上でジョブがまだ実行中の可能性があるので、
    // 楽観的に空へせず invalidate で再取得する（移設元 :1245-1257 のコメント）
    void queryClient.invalidateQueries({ queryKey: ['useQueryLatestAlgorithmRequestVersion'] });
    void queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY] });
    void queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY] });
    void queryClient.invalidateQueries({ queryKey: [SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY] });
  }, [queryClient]);

  // polling 完了時: キャッシュ invalidate → overrides クリア。移設元 Presenter.tsx:1259-1384。
  //
  // 注意: override をクリアするタイミングが deliveries の refetch 完了より早いと、
  // 古いキャッシュ + 空 override で一瞬 render されてユーザのドロップ位置が元の位置に
  // 戻って見えてしまう。そのため deliveries の refetch を await してから
  // override / legRuns 関連のキャッシュ更新を行う。
  useEffect(() => {
    if (!planningResultData && !planningResultError) return;

    if (planningResultData) {
      const notAllocIds = planningResultData.not_allocated_order_ids ?? [];
      const normalized = normalizePollingResult(planningResultData);
      void (async () => {
        // scope 変更で追い越されていれば、ここで処理を始めない（新規部分。設計書 :313-336）。
        // generation だけでなく captured scope も併せて照合する二重照合
        const owningRun = activeRequest?.run ?? null;
        const liveRun: RunIdentity = { generation: generationRef.current, scope: scopeRef.current };
        if (!isSameRun(liveRun, owningRun)) return;

        const mySessionToken = activePollingSessionRef.current;
        const mode = activeModeRef.current ?? 'normal';

        // version 更新はキャッシュに影響しないので即時 OK（現行 :1282-1283）
        setLatestAlgorithmRequestVersion(normalized.latestAlgorithmRequestVersion ?? 0);
        void queryClient.invalidateQueries({ queryKey: ['useQueryLatestAlgorithmRequestVersion'] });

        // deliveries の refetch を await する。この await 中は override が残ったままなので、
        // ユーザのドロップ位置が表示され続け、元の位置に戻って見える flicker を防げる。
        try {
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY] }),
            queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY] }),
          ]);
        } catch (e) {
          console.warn('[autoDispatch] deliveries refetch failed', e);
        }

        // await 中に cancel が入ったら以降を中断する（現行 activePollingSessionRef・:1276-1279。
        // ref を消すのは cancel なので、中断の経路は cancel である）
        if (activePollingSessionRef.current !== mySessionToken) return;

        // すべての await 後に generation + captured scope を再照合する（設計書 :319-320）。
        // activePollingSessionRef のクリアは通常の useEffect（passive）で一拍遅れるため、
        // await 中に scope が変わった場合の窓をここで塞ぐ（Task 4 レビューで論点1として指摘）。
        //
        // Why not このタスクのテストで検出できると期待しない: 活動中の run は
        // generation・scope・activePollingSessionRef が必ず対で変化する（scope 変更・cancel の
        // どちらも同時にこの 3 つをクリアする）ため、この行を消しても直前の
        // activePollingSessionRef 照合が同じシナリオを独立に検出してしまい、
        // 単独で検出するテストが作れない（実測・[実験] を一時追加して確認済み）。
        // それでも残すのは、設計書 :319-320 が要求する二重照合を文字どおり満たすため
        // （Task 4 レビュー・論点1でフェーズ B 送りを覆された判断）
        const liveRunAfterAwait: RunIdentity = { generation: generationRef.current, scope: scopeRef.current };
        if (!isSameRun(liveRunAfterAwait, owningRun)) return;

        markLegRunsUnassigned(notAllocIds);
        void queryClient.invalidateQueries({ queryKey: [SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY] });

        // overrides はサーバデータに置き換える（refetch 完了後なので flicker しない）
        if (hooksRef.current.shouldClearLocalOverrides()) {
          hooksRef.current.clearLocalOverrides();
        }

        // sessionId は割当不可理由の参照に使うので、run 追跡用の mySessionToken
        // （scenario では scenario_planning_id になりうる）ではなく、polling 応答が
        // 返す本物の session_id から取る（Task 0。`normalizePollingResult` 参照）
        const result: AutoDispatchResult = {
          notAllocatedOrderIds: notAllocIds,
          sessionId: normalized.sessionId,
          latestAlgorithmRequestVersion: normalized.latestAlgorithmRequestVersion,
        };
        hooksRef.current.onCompleted(result, { mode, completionKind: 'queued' });

        // 完了 → cancel API 用の目印もクリア（以降の応答は別 job として扱う）
        setActiveRequest(null);
        setIsRunning(false);
        activeRunRef.current = null;
        activePollingSessionRef.current = null;
        runningPlanningIdRef.current = null;

        // 地図画面などと共有している running 一覧キャッシュを空にする（現行 :1367-1375）。
        // 実行中に表示日付を変えていても、ジョブ開始時の日付を対象にする
        const completedRange = runningPlanningRangeRef.current;
        runningPlanningRangeRef.current = null;
        if (completedRange) {
          void clearRunningPlanningsCache(queryClient, completedRange.startOn, completedRange.endOn);
        }
      })();
    } else if (planningResultError) {
      const mode = activeModeRef.current ?? 'normal';
      // 画面固有の後片付け・通知は onFailed 側（override の破棄は shouldClearLocalOverrides
      // を経由せず onFailed 実装の中で行う。設計書「失敗時の分担」）
      hooksRef.current.onFailed(planningResultError, { mode, phase: 'polling' });
      recoverAfterFailure();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planningResultData, planningResultError, queryClient, activeRequest, recoverAfterFailure]);

  // 即時完了（normal / transfer の queued でない応答）の後始末。polling 完了と
  // 中身が違う（現行 :2737-2779。設計書「即時完了の後始末はpolling完了と違う」）。
  // - running キャッシュは「現在の scope の日付」（ジョブ開始時の日付ではない）
  // - override は無条件クリアだが、それは画面側の onCompleted が行う
  //   （shouldClearLocalOverrides を通すのは queued 完了だけ）
  const completeImmediately = (mode: AutoDispatchMode, raw: RawInitialResponse) => {
    // timeline の即時完了には割当不可 ID の概念が無い（設計書 :628・現行 :2012-2022 は
    // not_allocated_order_ids を読まない）。normal/transfer だけが対象
    const notAllocIds = mode === 'timeline' ? [] : (raw.not_allocated_order_ids ?? []);

    void clearRunningPlanningsCache(queryClient, scopeRef.current.startOn, scopeRef.current.endOn);
    markLegRunsUnassigned(notAllocIds);
    void queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY] });
    void queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY] });
    void queryClient.invalidateQueries({ queryKey: [SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY] });
    void queryClient.invalidateQueries({ queryKey: ['useQueryLatestAlgorithmRequestVersion'] });

    hooksRef.current.onCompleted(
      {
        notAllocatedOrderIds: notAllocIds,
        sessionId: raw.session_id ?? null,
        latestAlgorithmRequestVersion: null,
      },
      { mode, completionKind: 'immediate' },
    );

    activeRunRef.current = null;
    setIsRunning(false);
    runningPlanningRangeRef.current = null;
  };

  /**
   * キャンセル API (DELETE /api/v3/plannings/running/:id) に渡す running ID を取る。
   *
   * V3Plans の「実行を停止する」(V3Plans/Presenter.tsx:1026-1035) と同じく、
   * running 一覧の先頭を対象にする。これが無いと cancel は画面だけ「止まった」ことにして
   * サーバ側は計算を続けてしまう。
   *
   * 別の非同期で遅れて書き込むため、書き込み時点で現在の run と一致するか確認する
   * （確認しないと「range 欠落は到達不能」の前提が崩れる）。
   */
  const captureRunningPlanningId = (run: RunIdentity) => {
    void (async () => {
      try {
        const refresh = await runningPlanningsQuery.refetch();
        if (!isSameRun(activeRunRef.current, run)) return;
        const running = refresh.data?.[0];
        if (running?.id != null) {
          runningPlanningIdRef.current = running.id;
        }
      } catch (e) {
        console.warn('[autoDispatch] failed to fetch running planning id', e);
      }
    })();
  };

  // normal/transfer/timeline/scenario 共通の初期応答ハンドラ。queued なら polling へ載せ、
  // 失敗（scenario の非 queued のみ）なら onFailed へ、そうでなければ即時完了する。
  //
  // queued が確定したときだけ running ID を取りにいく。即時完了した run は止める対象が
  // 無く、撃つと他人の job を消しうる。
  //
  // onQueued はモードごとに異なる「queued 確定後だけ」の追加処理を外から注入する差し込み口。
  // scenario の onStarted 呼び出し（下記 runScenario 参照。設計書:212-222「onStarted は
  // queued を確認してから」）に使う。
  const handleInitialResponse = (
    mode: AutoDispatchMode,
    run: RunIdentity,
    raw: RawInitialResponse,
    onQueued?: () => void,
  ) => {
    // HTTP 往復中に scope 変更等で追い越されていれば、応答を無視する
    if (!isSameRun(activeRunRef.current, run)) return;

    hooksRef.current.onInitialResponse?.({ mode });
    const normalized = normalizeInitialResponse(mode, raw);

    // scenario だけの経路。HTTP は成功したが失敗として扱う（現行
    // useScenarioPlanningRequestV5.ts:38。throw されないので後始末を書き忘れても
    // 気づきにくい特殊な経路）
    if (normalized.kind === 'failed') {
      hooksRef.current.onFailed(new Error('scenario planning did not queue'), { mode, phase: 'request' });
      recoverAfterFailure();
      return;
    }

    if (normalized.kind !== 'queued') {
      completeImmediately(mode, raw);
      return;
    }

    // scenario は session_id を持たず scenario_planning_id で polling する（whole-branch
    // レビュー I-2。POST /api/v5/scenario_plannings の応答は session_id を含まない）。
    // この ref は「この run の応答を追跡するための token」として使うだけなので、
    // 3 つのうちどれが来ても構わない
    activePollingSessionRef.current = normalized.sessionId ?? normalized.scenarioPlanningId ?? normalized.respectRecentId;
    setActiveRequest({
      payload: {
        status: 'queued',
        session_id: normalized.sessionId ?? undefined,
        scenario_planning_id: normalized.scenarioPlanningId ?? undefined,
        respect_recent_id: normalized.respectRecentId ?? undefined,
        splitted_orders: { original_order_ids: [], split_order_ids: [], split_orders: [] },
      },
      run,
    });
    // queued になった run だけが「止められる run」。全モード共通で ID を取りにいく
    captureRunningPlanningId(run);
    onQueued?.();
  };

  const runNormal = useCallback((input: RunNormalInput) => {
    // 多重起動ガード
    if (activeRunRef.current !== null) return;

    generationRef.current += 1;
    const run: RunIdentity = { generation: generationRef.current, scope: scopeRef.current };
    activeRunRef.current = run;
    activeModeRef.current = 'normal';
    runningPlanningRangeRef.current = { startOn: scopeRef.current.startOn, endOn: scopeRef.current.endOn };
    setIsRunning(true);

    hooksRef.current.onRunAccepted({ mode: 'normal' });
    // onStarted は request の直前（設計書 :771-779）
    hooksRef.current.onStarted({ mode: 'normal', orderIds: input.orderIds });

    // 送信の直前に版番号を取り直す（timeline と同じ。下記 runTimeline 参照）。
    // 止めた直後は planning が cancelled になってサーバ側の版番号が上がっており、
    // 古い版で撃つと「同時にリクエストが行われた可能性があるため、配車ができませんでした。」
    // になる。キャンセル時の取り直しだけでは、直後に押されると間に合わない
    void (async () => {
      const freshVersion = await resolveFreshVersionRef.current(latestAlgorithmRequestVersion);
      if (!isSameRun(activeRunRef.current, run)) return;

      normalMutation.mutate(
        { ...input, latestAlgorithmRequestVersion: freshVersion },
        {
          onSuccess: (response) => { handleInitialResponse('normal', run, response.data); },
          onError: (error: unknown) => {
            if (!isSameRun(activeRunRef.current, run)) return;
            hooksRef.current.onFailed(error, { mode: 'normal', phase: 'request' });
            recoverAfterFailure();
          },
        },
      );
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalMutation, latestAlgorithmRequestVersion, recoverAfterFailure]);

  const runTransfer = useCallback(async (input: RunTransferInput) => {
    // 多重起動ガード
    if (activeRunRef.current !== null) return;

    generationRef.current += 1;
    const run: RunIdentity = { generation: generationRef.current, scope: scopeRef.current };
    activeRunRef.current = run;
    activeModeRef.current = 'transfer';
    runningPlanningRangeRef.current = { startOn: scopeRef.current.startOn, endOn: scopeRef.current.endOn };
    setIsRunning(true);

    hooksRef.current.onRunAccepted({ mode: 'transfer' });
    // onStarted は前段 reset の直前（通知してから reset を撃つ。設計書 :771-779）
    hooksRef.current.onStarted({ mode: 'transfer', orderIds: input.orderIds });

    try {
      await resettingMutation.mutateAsync(input.orderIds);
    } catch (error) {
      if (!isSameRun(activeRunRef.current, run)) return;
      hooksRef.current.onFailed(error, { mode: 'transfer', phase: 'reset' });
      recoverAfterFailure();
      return;
    }

    if (!isSameRun(activeRunRef.current, run)) return;

    transferMutation.mutate(
      { ...input, latestAlgorithmRequestVersion },
      {
        onSuccess: (response) => { handleInitialResponse('transfer', run, response.data); },
        onError: (error: unknown) => {
          if (!isSameRun(activeRunRef.current, run)) return;
          hooksRef.current.onFailed(error, { mode: 'transfer', phase: 'request' });
          recoverAfterFailure();
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resettingMutation, transferMutation, latestAlgorithmRequestVersion, recoverAfterFailure]);

  // 移設元: runAutoDispatch（Presenter.tsx:1687-2051）。debounce・override 判定・
  // timelineEntities/emptyDeliveryIds/isUnassignOnly の導出はすべて画面に残る
  // （設計書「debounce は controller に入れない」）。ここが受け取るのは構築済みの入力だけ
  const runTimeline = useCallback(async (input: RunTimelineInput) => {
    // 多重起動ガード
    if (activeRunRef.current !== null) return;

    generationRef.current += 1;
    const run: RunIdentity = { generation: generationRef.current, scope: scopeRef.current };
    activeRunRef.current = run;
    activeModeRef.current = 'timeline';
    runningPlanningRangeRef.current = { startOn: scopeRef.current.startOn, endOn: scopeRef.current.endOn };
    setIsRunning(true);

    hooksRef.current.onRunAccepted({ mode: 'timeline' });

    // row→unassigned のドロップを先に戻す（現行 :1809-1841）。無ければ撃たない
    if (input.resetLegRunIds.length > 0) {
      try {
        await resettingMutation.mutateAsync(input.resetLegRunIds);
      } catch (error) {
        if (!isSameRun(activeRunRef.current, run)) return;
        // timeline は transfer と文言の契約が違う（設計書 :749）が、判断は画面側の
        // onFailed 実装に任せる。controller は phase だけ渡す
        hooksRef.current.onFailed(error, { mode: 'timeline', phase: 'reset' });
        recoverAfterFailure();
        return;
      }
      if (!isSameRun(activeRunRef.current, run)) return;
      // 現行 :1824。落とすと未割当へ戻した legRun が画面から消えたままになる
      applyResetToCaches(input.resetLegRunIds);
    }

    // 終わり方 A: 純 unassign（現行 :1834-1841）。timelinePlanning を撃たず override だけ
    // 画面側が消す（shouldClearLocalOverrides を経由しない。onCompleted に completionKind を渡す）
    if (input.isUnassignOnly) {
      hooksRef.current.onCompleted(
        { notAllocatedOrderIds: [], sessionId: null, latestAlgorithmRequestVersion: null },
        { mode: 'timeline', completionKind: 'skipped' },
      );
      activeRunRef.current = null;
      setIsRunning(false);
      runningPlanningRangeRef.current = null;
      return;
    }

    // 終わり方 B: 両方空（現行 :1963-1966）。黙って終了する。onCompleted も呼ばない
    if (input.timelineEntities.length === 0 && input.emptyDeliveryIds.length === 0) {
      activeRunRef.current = null;
      setIsRunning(false);
      runningPlanningRangeRef.current = null;
      return;
    }

    // onStarted は前段 reset の完了後、timeline request の直前（純 unassign の return が
    // 通知より前にあるため・設計書 :775）。timeline に orderIds の概念は無いので空で渡す
    hooksRef.current.onStarted({ mode: 'timeline', orderIds: [] });

    // timeline だけ mutate 直前に version を再取得する（現行 :1971-1983）。runNormal との
    // 非対称が本番の姿（debounce + キャンセル→再実行で古い version のまま撃つと
    // バージョン不整合エラーになるため）
    let freshVersion = latestAlgorithmRequestVersion;
    try {
      const refresh = await versionQuery.refetch() as { data?: unknown };
      if (typeof refresh.data === 'number') {
        freshVersion = refresh.data;
        setLatestAlgorithmRequestVersion(refresh.data);
      }
    } catch (e) {
      console.warn('[autoDispatch] failed to refetch latest algorithm version (use cached)', e);
    }

    if (!isSameRun(activeRunRef.current, run)) return;

    timelineMutation.mutate(
      {
        startOn: scopeRef.current.startOn,
        endOn: scopeRef.current.endOn,
        latestAlgorithmRequestVersion: freshVersion,
        timelineEntities: input.timelineEntities,
        emptyDeliveryIds: input.emptyDeliveryIds,
      },
      {
        onSuccess: (response) => {
          // running ID の取得は handleInitialResponse が全モード共通で行う
          handleInitialResponse('timeline', run, response.data);
        },
        onError: (error: unknown) => {
          if (!isSameRun(activeRunRef.current, run)) return;
          hooksRef.current.onFailed(error, { mode: 'timeline', phase: 'request' });
          recoverAfterFailure();
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    resettingMutation, timelineMutation, latestAlgorithmRequestVersion, recoverAfterFailure,
    applyResetToCaches, versionQuery, runningPlanningsQuery,
  ]);

  // 新規モード（現行のカード配車には無い）。移設元は useScenarioPlanningRequestV5.ts:26-45
  /**
   * 配送計画の入れ替え。queued で返れば normal/transfer と同じくポーリングへ入る。
   * controller に乗せないと「入れ替えました」と出たまま結果が反映されない。
   * 前段の reset は無い（入れ替えは既存の割当を保ったまま 2 勤務を交換するため）。
   */
  const runSwap = useCallback((input: RunSwapInput) => {
    // 多重起動ガード
    if (activeRunRef.current !== null) return;

    generationRef.current += 1;
    const run: RunIdentity = { generation: generationRef.current, scope: scopeRef.current };
    activeRunRef.current = run;
    activeModeRef.current = 'swap';
    runningPlanningRangeRef.current = { startOn: scopeRef.current.startOn, endOn: scopeRef.current.endOn };
    setIsRunning(true);

    hooksRef.current.onRunAccepted({ mode: 'swap' });
    // onStarted は request の直前（他の run* と同じ）。入れ替えは対象を deliveryId で
    // 指定するので orderIds は無い
    hooksRef.current.onStarted({ mode: 'swap', orderIds: [] });

    swapMutate(
      {
        deliveryIds: input.deliveryIds,
        latestAlgorithmRequestVersion,
        startOn: scopeRef.current.startOn,
        endOn: scopeRef.current.endOn,
      },
      {
        onSuccess: (response) => { handleInitialResponse('swap', run, response.data); },
        onError: (error: unknown) => {
          if (!isSameRun(activeRunRef.current, run)) return;
          hooksRef.current.onFailed(error, { mode: 'swap', phase: 'request' });
          recoverAfterFailure();
        },
      },
    );
     
    // handleInitialResponse は他の run* と同じく依存に入れない（毎レンダー作り直され、
    // 入れると runSwap も作り直されるため。runNormal/runTransfer と同じ扱い）。
    // useMutation の戻り値は毎レンダー新しいオブジェクトなので mutate を依存にする
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swapMutate, latestAlgorithmRequestVersion, recoverAfterFailure]);

  const runScenario = useCallback((input: RunScenarioInput) => {
    // 多重起動ガード
    if (activeRunRef.current !== null) return;

    generationRef.current += 1;
    const run: RunIdentity = { generation: generationRef.current, scope: scopeRef.current };
    activeRunRef.current = run;
    activeModeRef.current = 'scenario';
    runningPlanningRangeRef.current = { startOn: scopeRef.current.startOn, endOn: scopeRef.current.endOn };
    setIsRunning(true);

    hooksRef.current.onRunAccepted({ mode: 'scenario' });
    // onStarted はここでは呼ばない。queued を確認してから handleInitialResponse の
    // onQueued 経由で呼ぶ（設計書:212-222。他モードと違い request 直前ではない）

    scenarioMutation.mutate(
      { scenarioId: input.scenarioId, startOn: scopeRef.current.startOn, endOn: scopeRef.current.endOn },
      {
        onSuccess: (response) => {
          handleInitialResponse('scenario', run, response.data, () => {
            hooksRef.current.onStarted({ mode: 'scenario', orderIds: [] });
          });
        },
        onError: (error: unknown) => {
          if (!isSameRun(activeRunRef.current, run)) return;
          hooksRef.current.onFailed(error, { mode: 'scenario', phase: 'request' });
          recoverAfterFailure();
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarioMutation, recoverAfterFailure]);

  const cancel = useCallback((options?: { notifyOnFailure?: boolean }) => {
    // 失敗を伝えるのは、呼び出し元がそれを求めたときだけ（型定義の Why 参照）
    const onCancelFailure = options?.notifyOnFailure ? notifyCancelFailure : undefined;
    // 実行していなければ何もしない（現行 :1125）
    if (activeRunRef.current === null) return;

    // 停止したことを画面へ知らせる。/map は隠した未割当を戻す（選択は解除しない）。
    // range が無くて早期 return する経路でも「止めた」ことは変わらないので先に呼ぶ
    hooksRef.current.onCancelled?.({ mode: activeModeRef.current ?? 'normal' });

    const range = runningPlanningRangeRef.current;
    const id = runningPlanningIdRef.current;
    // queued の応答で入る token。null なら POST がまだ返っていない
    const accepted = activePollingSessionRef.current;

    // 破棄（Task 3 で入っている部分）。override は消さない — shouldClearLocalOverrides は
    // 「結果が返って完了したとき」専用の述語であり、cancel では見ない（現行 :1145-1150。
    // ユーザーの D&D 位置を保持するため。ここで override を消すと退行になる）
    generationRef.current += 1;
    activeRunRef.current = null;
    activePollingSessionRef.current = null;
    runningPlanningIdRef.current = null;
    runningPlanningRangeRef.current = null;
    setActiveRequest(null);
    setIsRunning(false);

    // range は活動中の run に必ず対で存在する（run* が同時にセットするため）。
    // 「range だけ欠ける」経路（現行 :1129-1137 の警告分岐）はこの設計では到達しない
    if (!range) return;

    // 止めた時点でサーバの状態は動いている。V3Plans は queryClient.clear() で全部
    // 捨て直す (V3Plans/Component.tsx:533)。ここでは影響範囲を絞って、
    // 版番号・配車・案件だけを取り直す
    refreshAlgorithmVersionRef.current();
    refreshServerDeliveries();

    // DELETE 応答を待つ間も busy 扱いにする（sendCancelDelete 自体は isBusy に乗せない
    // 設計なので、cancel() 側だけ isReconciling で明示的に立てる）
    setIsReconciling(true);

    if (id !== null) {
      sendCancelDelete(id, range, () => setIsReconciling(false), onCancelFailure);
      return;
    }

    // サーバがまだ受け付けていない run は、止める対象が存在しない。ここで一覧を引いて
    // 先頭を消すと、無関係な（他の人が回している）job を止めてしまう
    if (accepted === null) {
      setIsReconciling(false);
      void invalidateRunningPlanningsCache(queryClient, range.startOn, range.endOn);
      refreshServerDeliveries();
      return;
    }

    // ID をまだ掴めていない場合は、その場で running 一覧を引いて対象を決める。
    // V3Plans の「実行を停止する」(V3Plans/Presenter.tsx:1028) も押した時点の一覧から
    // 対象を選ぶ。queued 直後の 1 回だけで掴む作りだと、取得が返る前に押されたときや
    // 取りこぼしたときに DELETE が飛ばず、画面だけ止まってサーバは計算を続ける
    void (async () => {
      try {
        const refresh = await runningPlanningsQuery.refetch();
        const running = refresh.data?.[0];
        if (running?.id != null) {
          sendCancelDelete(running.id, range, () => setIsReconciling(false), onCancelFailure);
          return;
        }
      } catch (e) {
        console.warn('[autoDispatch] failed to resolve running planning id on cancel', e);
      }
      // 対象が引けなければ終端を待たずに invalidate して終わる
      setIsReconciling(false);
      void invalidateRunningPlanningsCache(queryClient, range.startOn, range.endOn);
      refreshServerDeliveries();
    })();
    // runningPlanningsQuery は毎レンダー新しいオブジェクトだが refetch は安定なので依存に入れない
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, sendCancelDelete, refreshServerDeliveries, notifyCancelFailure]);

  return {
    isRunning,
    isReconciling,
    isBusy: isRunning || isReconciling,
    /**
     * サーバが受け付けて計算に入ったか（queued 確定〜終了まで）。
     * 受け付け前は止める対象が無いので、「実行中」の表示と停止操作はこれで出し分ける。
     */
    isAccepted: activeRequest !== null,
    cancel,
    runNormal,
    runTransfer,
    runTimeline,
    runScenario,
    runSwap,
  };
}
